import { useEffect, useMemo, useRef, useState } from "react";
import { supabase, isSupabaseConfigured } from "../lib/supabase.js";

/* ─── Account / access state ─────────────────────────────────────
   Single source of truth for who the user is and what they can do.
   Backed by Supabase Auth (email OTP) + the tables created in
   supabase/migrations/ (profiles, access_grants, user_activity,
   admins). See docs/ACCOUNTS.md for the model and admin workflows. */

const APP_VERSION = typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "0.0.0";

/* A referral link (?ref=CODE) almost always lands on someone with no account
   yet, so the code has to survive the whole sign-up round trip — including the
   emailed magic link, which reopens the app on a fresh URL. Park it here on
   arrival and redeem once a session exists. */
const PENDING_REF_KEY = "aurio-pending-ref";

export function captureReferralFromUrl() {
  if (typeof window === "undefined") return null;
  try {
    const code = new URLSearchParams(window.location.search).get("ref");
    if (!code) return localStorage.getItem(PENDING_REF_KEY);
    const clean = code.trim().toUpperCase().slice(0, 12);
    if (clean) localStorage.setItem(PENDING_REF_KEY, clean);
    /* Strip it from the address bar so a shared screenshot or a bookmark of
       this page doesn't carry someone else's code around. */
    const url = new URL(window.location.href);
    url.searchParams.delete("ref");
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    return clean;
  } catch {
    return null;
  }
}
const readPendingRef = () => {
  try { return localStorage.getItem(PENDING_REF_KEY); } catch { return null; }
};
const clearPendingRef = () => {
  try { localStorage.removeItem(PENDING_REF_KEY); } catch { /* non-fatal */ }
};

export const PLANS = ["free", "trial", "tester", "paid", "lifetime"];
export const STATUSES = ["active", "expired", "cancelled"];

/* Pure derivation: grant row → effective access. Trial expiry is
   computed client-side so an out-of-date status column can't grant
   premium past the trial date. */
export function deriveAccess(grant) {
  const base = {
    plan: "free",
    status: "active",
    premiumEnabled: false,
    trialExpired: false,
    trialExpiresAt: null,
    trialStartedAt: null,
    canStartTrial: false,
    trialDaysLeft: null,
    premiumUntil: null,
    creditDaysLeft: null,
    isTester: false,
    isLifetime: false,
  };
  if (!grant) return base;
  const plan = grant.plan || "free";
  const trialExpiresAt = grant.trial_expires_at || null;
  const trialStartedAt = grant.trial_started_at || null;
  const trialExpired =
    plan === "trial" && trialExpiresAt != null && new Date(trialExpiresAt).getTime() < Date.now();
  const status = trialExpired && grant.status === "active" ? "expired" : grant.status || "active";
  /* Referral credit (premium_until) is orthogonal to plan: it stacks, and it
     keeps Premium on even for a free user or one whose trial has ended. Like
     trial expiry it's evaluated client-side against the clock, so a stale
     column can never over-grant. */
  const premiumUntil = grant.premium_until || null;
  const creditActive = premiumUntil != null && new Date(premiumUntil).getTime() > Date.now();
  const creditDaysLeft = creditActive
    ? Math.max(0, Math.ceil((new Date(premiumUntil).getTime() - Date.now()) / 86400000))
    : null;
  const premiumEnabled =
    creditActive ||
    (status === "active" && !trialExpired && ["trial", "tester", "paid", "lifetime"].includes(plan));
  /* Mirrors the guards inside start_self_trial() so the UI only offers what
     the database would actually grant. The DB stays the authority — this is
     presentation, never permission: a stale or tampered client just gets a
     refusal from the function. */
  const canStartTrial = plan === "free" && status === "active" && trialStartedAt == null;
  const trialDaysLeft =
    plan === "trial" && trialExpiresAt && !trialExpired
      ? Math.max(0, Math.ceil((new Date(trialExpiresAt).getTime() - Date.now()) / 86400000))
      : null;
  return {
    plan,
    status,
    premiumEnabled,
    trialExpired,
    trialExpiresAt,
    trialStartedAt,
    canStartTrial,
    trialDaysLeft,
    premiumUntil,
    creditDaysLeft,
    isTester: plan === "tester",
    isLifetime: plan === "lifetime",
  };
}

function platformLabel() {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/mac/i.test(ua)) return "macos";
  if (/win/i.test(ua)) return "windows";
  if (/linux/i.test(ua)) return "linux";
  return "web";
}

/* Privacy-conscious heartbeat: version + coarse platform only. */
async function touchActivity(isLogin) {
  if (!supabase) return;
  try {
    await supabase.rpc("touch_activity", {
      p_app_version: APP_VERSION,
      p_platform: platformLabel(),
      p_login: isLogin,
    });
  } catch (err) {
    console.warn("[Aurio] activity ping failed", err);
  }
}

export function useAccount() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [grant, setGrant] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [grantVersion, setGrantVersion] = useState(0);
  /* Set when a referral lands, so the app can celebrate it once and clear. */
  const [referralEvent, setReferralEvent] = useState(null);

  useEffect(() => {
    if (!supabase) return;
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (event === "SIGNED_IN") touchActivity(true);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const userId = session?.user?.id || null;

  useEffect(() => {
    if (!supabase || !userId) {
      setProfile(null);
      setGrant(null);
      setIsAdmin(false);
      return;
    }
    let mounted = true;
    (async () => {
      const [p, g, a] = await Promise.all([
        supabase.from("profiles").select("*").eq("id", userId).maybeSingle(),
        supabase.from("access_grants").select("*").eq("user_id", userId).maybeSingle(),
        supabase.from("admins").select("user_id").eq("user_id", userId).maybeSingle(),
      ]);
      if (!mounted) return;
      setProfile(p.data || null);
      setGrant(g.data || null);
      setIsAdmin(Boolean(a.data));
    })();
    touchActivity(false);
    return () => {
      mounted = false;
    };
  }, [userId, grantVersion]);

  /* Referral catch-up on sign-in — the same shape as the recurring-bill and
     interest catch-ups the app already runs on open, so the loop needs no cron
     and no edge function.

     1. Redeem a code parked by a ?ref= link (survives the magic-link round trip).
     2. Settle any of THIS user's referrals whose friend has now passed the
        7-day activity bar.

     Runs once per signed-in session (`settledFor`), and a rule-based refusal
     ("already used", "account too old") clears the parked code so it can't
     retry forever. A network failure leaves it parked to try again later. */
  const settledFor = useRef(null);
  useEffect(() => {
    if (!supabase || !userId || settledFor.current === userId) return;
    settledFor.current = userId;
    let mounted = true;
    (async () => {
      const pending = readPendingRef();
      if (pending) {
        const { data, error } = await supabase.rpc("redeem_referral", { p_code: pending });
        if (!error) {
          clearPendingRef();
          const row = Array.isArray(data) ? data[0] : data;
          if (mounted && row) { setGrant(row); setReferralEvent({ type: "redeemed" }); }
        } else if (error.code === "P0001" || error.code === "28000") {
          clearPendingRef(); // a rule said no — retrying will never help
          console.info("[Aurio] referral not applied:", error.message);
        }
      }
      const { data: paid, error: sErr } = await supabase.rpc("settle_my_referrals");
      if (!sErr && mounted && paid > 0) {
        setGrantVersion((v) => v + 1);
        setReferralEvent({ type: "rewarded", months: paid });
      }
    })();
    return () => { mounted = false; };
  }, [userId]);

  const access = useMemo(() => deriveAccess(grant), [grant]);

  return {
    configured: isSupabaseConfigured,
    loading,
    session,
    user: session?.user || null,
    profile,
    grant,
    access,
    isAdmin,

    /* Email OTP: one flow covers both signup and login. Password auth
       can be added later via supabase.auth.signInWithPassword without
       touching this surface. */
    requestCode: (email) =>
      supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: true, emailRedirectTo: window.location.origin },
      }),
    verifyCode: (email, token) => supabase.auth.verifyOtp({ email, token, type: "email" }),
    signOut: () => supabase.auth.signOut(),

    saveName: async (fullName) => {
      const { error } = await supabase.from("profiles").update({ full_name: fullName }).eq("id", userId);
      if (!error) setProfile((prev) => (prev ? { ...prev, full_name: fullName } : prev));
      return { error };
    },

    /* Re-pull the access grant — used after returning from Stripe Checkout
       so the just-purchased plan shows without a full reload. */
    refreshAccess: () => setGrantVersion((v) => v + 1),

    /* Self-serve 14-day trial. Every guard (free + active, never trialled,
       fixed duration) lives in the SECURITY DEFINER function — this call
       can't choose its own expiry or grant itself a second trial, so a
       tampered client gains nothing. The returned row is written straight
       into state so Premium unlocks without a reload. */
    startTrial: async () => {
      if (!supabase) return { error: { message: "not configured" } };
      const { data, error } = await supabase.rpc("start_self_trial");
      if (error) return { error };
      const row = Array.isArray(data) ? data[0] : data;
      if (row) setGrant(row); else setGrantVersion((v) => v + 1);
      return { grant: row };
    },

    /* ── referrals ────────────────────────────────────────────────
       "Give a friend a month, get a month." The friend's month lands on
       redemption; the referrer's is settled only once that friend is still
       active a week later, so a throwaway email earns nothing. Both sides
       are enforced server-side — see supabase/migrations/*_referrals.sql. */
    myReferralCode: async () => {
      if (!supabase) return { error: { message: "not configured" } };
      const { data, error } = await supabase.rpc("my_referral_code");
      return error ? { error } : { code: data };
    },

    referralStats: async () => {
      if (!supabase || !userId) return { rewarded: 0, pending: 0 };
      const { data, error } = await supabase
        .from("referrals")
        .select("rewarded_at")
        .eq("referrer_user_id", userId);
      if (error || !data) return { rewarded: 0, pending: 0 };
      return {
        rewarded: data.filter((r) => r.rewarded_at).length,
        pending: data.filter((r) => !r.rewarded_at).length,
      };
    },

    redeemReferral: async (code) => {
      if (!supabase) return { error: { message: "not configured" } };
      const { data, error } = await supabase.rpc("redeem_referral", { p_code: code });
      if (error) return { error };
      clearPendingRef();
      const row = Array.isArray(data) ? data[0] : data;
      if (row) setGrant(row); else setGrantVersion((v) => v + 1);
      return { grant: row };
    },

    pendingReferral: readPendingRef,
    clearPendingReferral: clearPendingRef,
    referralEvent,
    clearReferralEvent: () => setReferralEvent(null),

    /* Billing: open Stripe-hosted Checkout / Billing Portal. Both call the
       edge functions with the user's access token; the functions verify it
       and never expose secret keys to the client. */
    startCheckout: async (plan) => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`, {
        method: "POST",
        headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan, origin: window.location.origin }),
      });
      const body = await res.json().catch(() => ({}));
      return { url: body.url, error: body.error };
    },
    openBillingPortal: async () => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/customer-portal`, {
        method: "POST",
        headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ origin: window.location.origin }),
      });
      const body = await res.json().catch(() => ({}));
      return { url: body.url, error: body.error };
    },

    /* Admin API — server-side guarded by is_admin(); these calls simply
       fail with an error for non-admins. */
    adminListUsers: () => supabase.rpc("admin_list_users"),

    /* Trial invites (pre-grant model): admin records email + days; the
       signup trigger upgrades that email to a trial on first sign-in.
       RLS restricts every operation to admins. */
    adminListInvites: () =>
      supabase.from("trial_invites").select("*").order("created_at", { ascending: false }),
    /* Real invite: calls the admin-invite edge function, which pre-grants
       AND creates the account immediately via Auth's inviteUserByEmail —
       so the person shows up under Users & access right away instead of
       only after they self-sign-up. */
    adminInvite: async ({ email, days }) => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-invite`, {
        method: "POST",
        headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, days, origin: window.location.origin }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) return { error: { message: body.error || "invite failed", alreadyExists: Boolean(body.alreadyExists) } };
      return { error: null, data: body };
    },
    adminRevokeInvite: (email) =>
      supabase.from("trial_invites").delete().eq("email", email).is("claimed_at", null),
    adminSetAccess: ({ userId: targetId, plan, status, trialExpiresAt, notes }) =>
      supabase.rpc("admin_set_access", {
        p_user_id: targetId,
        p_plan: plan,
        p_status: status,
        p_trial_expires_at: trialExpiresAt || null,
        p_notes: notes || null,
      }),
  };
}
